import { useCallback, useEffect, useState } from 'react';

import {
  faCheckCircle,
  faExclamationCircle,
  faExclamationTriangle,
  faSpinner,
  faTimes,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Autocomplete,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/index';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';

import { useCreateReferralByPatientId } from '@/react-query/useCreateReferralByPatientId';
import type { PatientSearchResult } from '@/react-query/usePatientSearch';
import { usePatientSearch } from '@/react-query/usePatientSearch';
import { usePreferences } from '@/react-query/usePreferences';
import {
  useResubmitReferral,
  useUpdateReferralById,
} from '@/react-query/useReferralInfo';
import { SparklesIcon } from '@/svgs/gradientIcons/sparklesIcon';
import { useAppContext } from '@/utils/hooks/useAppContext';
import { parseAndFormatDate } from '@/utils/parseDate';

import { SummaryCard } from '../../common-components/elements/SummaryCard/SummaryCard';
import routes from '../../routes/routes.constants';
import { PatientListPageLayout } from '../patientList/PatientListPageLayout';
import { LoadingSpinner } from '../patientList/styles';
import { PatientListPageLayoutContextMap } from '../patientList/types';

import { referralsSelectedStatusAtom } from './atoms';
import { ReferralsTable } from './components/ReferralsTable';
import { SpecialtySelectionModal } from './components/SpecialtySelectionModal';
import { SubmitReferralModal } from './components/SubmitReferralModal';
import { useReferralsData } from './hooks/useReferralsData';
import { useReferralsFilters } from './hooks/useReferralsFilters';
import { ReferralModal } from './ReferralModal';
import type { ReferralRecord } from './referralPageTypes';

// Helper function to format patient names by adding spaces between capital letters
const formatPatientName = (name: string): string => {
  if (!name) return '';
  // If the name already has spaces, return as is
  if (name.includes(' ')) return name;
  // Add space before capital letters (except the first one)
  return name.replace(/([a-z])([A-Z])/g, '$1 $2');
};

export const ReferralsPage = () => {
  const { t } = useLingui();
  const { data: preferences } = usePreferences();
  const shouldShowOldReferralFlow =
    preferences?.referralsPatientIdEnabled ?? false;

  // Card selection state
  const [selectedCard, setSelectedCard] = useState<
    'all' | 'submitted' | 'needsReferral' | 'failed'
  >('all');

  // Modal state
  const [isCreateReferralModalOpen, setIsCreateReferralModalOpen] =
    useState(false);
  const [referralAction, setReferralAction] = useState<'refer' | 'flag'>(
    'flag',
  );
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] =
    useState<PatientSearchResult | null>(null);
  const [isSpecialtyModalOpen, setIsSpecialtyModalOpen] = useState(false);
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const [isSubmitConfirmModalOpen, setIsSubmitConfirmModalOpen] =
    useState(false);
  const [isSubmitReferralModalOpen, setIsSubmitReferralModalOpen] =
    useState(false);
  const [selectedReferralForSubmit, setSelectedReferralForSubmit] =
    useState<ReferralRecord | null>(null);

  // Patient search
  const { data: patientSearchResults, isLoading: isSearchingPatients } =
    usePatientSearch(patientSearchTerm);

  // Get user context
  const { user } = useAppContext();

  // Create referral by patient ID
  const { mutate: createReferralByPatientId, isLoading: isCreatingReferral } =
    useCreateReferralByPatientId();

  // Initialize filter atoms (this will set up the filter options from API)
  useReferralsFilters();

  // Get setter and value for status atom
  const setSelectedStatus = useSetAtom(referralsSelectedStatusAtom);
  const selectedStatus = useAtomValue(referralsSelectedStatusAtom);

  // Sync selectedCard with status atom
  useEffect(() => {
    if (selectedStatus === 'all') {
      setSelectedCard('all');
    } else if (selectedStatus === 'submitted') {
      setSelectedCard('submitted');
    } else if (selectedStatus === 'needs_referral') {
      setSelectedCard('needsReferral');
    } else if (selectedStatus === 'failed') {
      setSelectedCard('failed');
    }
  }, [selectedStatus]);

  // Get all data and handlers from the custom hook
  const {
    referrals,
    metrics,
    pagination,
    hasError,
    isInitialLoading,
    isReferralsRefetching,
    lastAction,
    tableSortField,
    sortDirection,
    handlePageChange,
    handleSort,
    refetchReferrals,
  } = useReferralsData();

  const queryClient = useQueryClient();
  const {
    mutate: resubmitReferral,
    isLoading,
    isSuccess,
    isError,
    reset,
  } = useResubmitReferral();

  const { mutate: updateReferralById } = useUpdateReferralById();

  const handleResubmitReferral = useCallback(
    (referralId: string) => {
      resubmitReferral(referralId, {
        onSuccess: () => {
          queryClient.invalidateQueries(['referrals']);
        },
      });
    },
    [resubmitReferral, queryClient],
  );

  const handleUnflagReferral = useCallback(
    (referralId: string) => {
      updateReferralById(
        { referralId, data: { status: 'not_started' } },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(['referrals']);
          },
          onError: (error: any) => {
            console.error('Failed to unflag referral:', error);
            alert(
              error?.response?.data?.message ||
                'Failed to unflag referral. Please try again.',
            );
          },
        },
      );
    },
    [updateReferralById, queryClient],
  );

  // Card click handlers
  const handleCardClickAll = useCallback(() => {
    setSelectedCard('all');
    setSelectedStatus('all');
  }, [setSelectedStatus]);

  const handleCardClickSubmitted = useCallback(() => {
    setSelectedCard('submitted');
    setSelectedStatus('submitted');
  }, [setSelectedStatus]);

  const handleCardClickNeedsReferral = useCallback(() => {
    setSelectedCard('needsReferral');
    setSelectedStatus('needs_referral');
  }, [setSelectedStatus]);

  const handleCardClickFailed = useCallback(() => {
    setSelectedCard('failed');
    setSelectedStatus('failed');
  }, [setSelectedStatus]);

  // Navigation handler
  const handleNavigateToUnifiedAppointment = useCallback(
    (imagingPatientVideaId: string, unifiedAppointmentId: string) => {
      // Open the annotator view for this patient in a new tab
      const baseUrl = window.location.origin;
      const annotatorPath = `${routes.ANNOTATOR}/${imagingPatientVideaId}?unified-id=${unifiedAppointmentId}`;
      const fullUrl = `${baseUrl}/#${annotatorPath}`;
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    },
    [],
  );

  // Create referral handler
  const handleCreateReferral = () => {
    setIsCreateReferralModalOpen(true);
    // Reset search state when opening modal
    setReferralAction('flag');
    setPatientSearchTerm('');
    setSelectedPatient(null);
  };

  const handleCloseModal = () => {
    setIsCreateReferralModalOpen(false);
    setReferralAction('flag');
    setPatientSearchTerm('');
    setSelectedPatient(null);
  };

  const handleNext = useCallback(() => {
    if (!selectedPatient) return;

    // Close the create referral modal
    setIsCreateReferralModalOpen(false);

    if (referralAction === 'flag') {
      // Open specialty selection modal for flagging
      setIsSpecialtyModalOpen(true);
    } else {
      // Open referral modal for sending referral
      setIsReferralModalOpen(true);
    }
  }, [selectedPatient, referralAction]);

  const handleSpecialtyConfirm = useCallback(
    (specialtyId: number) => {
      if (!selectedPatient || !user?.primaryPracticeId) return;

      const requestData = {
        practiceId: user.primaryPracticeId,
        ...(selectedPatient.pmsPatientVideaId && {
          pmsPatientVideaId: selectedPatient.pmsPatientVideaId,
        }),
        ...(selectedPatient.imagingPatientVideaId && {
          imagingPatientVideaId: selectedPatient.imagingPatientVideaId,
        }),
        specialtyId,
        status: 'needs_referral' as const,
      };

      createReferralByPatientId(requestData, {
        onSuccess: () => {
          queryClient.invalidateQueries(['referrals']);
          setIsSpecialtyModalOpen(false);
          handleCloseModal();
        },
        onError: (error: any) => {
          console.error('Failed to create referral:', error);
          alert(
            error?.response?.data?.message ||
              'Failed to create referral. Please try again.',
          );
        },
      });
    },
    [
      selectedPatient,
      user?.primaryPracticeId,
      createReferralByPatientId,
      queryClient,
    ],
  );

  const handleSubmitReferral = (referral: ReferralRecord) => {
    setSelectedReferralForSubmit(referral);
    setIsSubmitConfirmModalOpen(true);
  };

  const handleSubmitConfirm = () => {
    setIsSubmitConfirmModalOpen(false);
    setIsSubmitReferralModalOpen(true);
  };

  const handleCloseSubmitModal = () => {
    setIsSubmitConfirmModalOpen(false);
    setSelectedReferralForSubmit(null);
  };

  return (
    <>
      <PatientListPageLayout
        context={PatientListPageLayoutContextMap.Referrals}
        isLoadingAppointments={isInitialLoading}
        isLoadingVisitSummaries={false}
        onCreateReferral={
          shouldShowOldReferralFlow ? handleCreateReferral : undefined
        }
      >
        <div className="vai-container vai-mx-auto vai-max-w-[1600px] vai-px-4 sm:vai-px-6 lg:vai-px-8">
          {hasError && (
            <div className="vai-bg-red-50 vai-border vai-border-red-200 vai-rounded-lg vai-p-4 vai-mb-6">
              <div className="vai-flex">
                <div className="vai-flex-shrink-0">
                  <FontAwesomeIcon
                    icon={faExclamationCircle}
                    className="vai-h-5 vai-w-5 vai-text-red-400"
                  />
                </div>
                <div className="vai-ml-3">
                  <h3 className="vai-text-sm vai-font-medium vai-text-red-800">
                    {t`Failed to load referrals data`}
                  </h3>
                  <p className="vai-mt-1 vai-text-sm vai-text-red-700">
                    {t`Please try refreshing the page. If the problem persists, contact support.`}
                  </p>
                  <button
                    onClick={() => refetchReferrals()}
                    className="vai-mt-2 vai-text-sm vai-text-red-800 vai-underline hover:vai-text-red-900"
                  >
                    {t`Try again`}
                  </button>
                </div>
              </div>
            </div>
          )}

          {!isInitialLoading && (
            <div className="vai-w-full vai-flex vai-gap-4 vai-mb-6 vai-justify-between">
              {/* "All referrals" card */}
              <SummaryCard
                icon={<SparklesIcon className="vai-w-6 vai-h-6" />}
                value={metrics.total}
                label={t`Total Referrals`}
                description={t`referrals submitted and flagged for action `}
                color="blue"
                isSelected={selectedCard === 'all'}
                isLoading={selectedCard === 'all' && isReferralsRefetching}
                onClick={handleCardClickAll}
                testId="referrals-summary-card"
              />

              {/* "Submitted" referrals card */}
              <SummaryCard
                icon={
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="vai-text-green-600 vai-w-6 vai-h-6"
                  />
                }
                value={metrics.submitted}
                label={t`Submitted`}
                description={t`referrals successfully submitted`}
                color="green"
                isSelected={selectedCard === 'submitted'}
                isLoading={
                  selectedCard === 'submitted' && isReferralsRefetching
                }
                onClick={handleCardClickSubmitted}
                testId="referrals-summary-card"
              />

              {/* "Needs referral" card */}
              <SummaryCard
                icon={
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="vai-text-amber-500 vai-w-6 vai-h-6"
                  />
                }
                value={metrics.needsReferral}
                label={t`Needs Referral`}
                description={t`referrals requiring action`}
                color="yellow"
                isSelected={selectedCard === 'needsReferral'}
                isLoading={
                  selectedCard === 'needsReferral' && isReferralsRefetching
                }
                onClick={handleCardClickNeedsReferral}
                testId="referrals-summary-card"
              />

              {/* "Failed" card */}
              <SummaryCard
                icon={
                  <FontAwesomeIcon
                    icon={faTimesCircle}
                    className="vai-text-red-500 vai-w-6 vai-h-6"
                  />
                }
                value={metrics.failed}
                label={t`Needs Resubmission`}
                description={t`referral failed to send, and needs resubmission`}
                color="red"
                isSelected={selectedCard === 'failed'}
                isLoading={selectedCard === 'failed' && isReferralsRefetching}
                onClick={handleCardClickFailed}
                testId="referrals-summary-card"
              />
            </div>
          )}

          {/* Resubmission Status Alert */}
          {(isLoading || isSuccess || isError) && (
            <Alert
              variant={isError ? 'destructive' : 'default'}
              className="vai-mb-4 vai-relative"
            >
              {isLoading && (
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="vai-h-4 vai-w-4 vai-animate-spin"
                />
              )}
              {isSuccess && (
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="vai-h-4 vai-w-4 vai-text-green-600"
                />
              )}
              {isError && (
                <FontAwesomeIcon
                  icon={faTimesCircle}
                  className="vai-h-4 vai-w-4"
                />
              )}
              <AlertTitle>
                {isLoading && <Trans>Resubmitting referral...</Trans>}
                {isSuccess && <Trans>Referral resubmitted successfully!</Trans>}
                {isError && <Trans>Resubmission failed</Trans>}
              </AlertTitle>
              <AlertDescription>
                {isLoading && (
                  <Trans>Please wait while we resubmit your referral.</Trans>
                )}
                {isSuccess && (
                  <Trans>
                    Your referral has been resubmitted successfully.
                  </Trans>
                )}
                {isError && (
                  <Trans>
                    Failed to resubmit referral. Please try again or contact
                    support.
                  </Trans>
                )}
              </AlertDescription>
              {(isSuccess || isError) && (
                <button
                  onClick={reset}
                  className="vai-absolute vai-top-2 vai-right-2 vai-p-1 vai-rounded-md hover:vai-bg-gray-100 vai-transition-colors"
                  aria-label="Dismiss alert"
                >
                  <FontAwesomeIcon icon={faTimes} className="vai-h-4 vai-w-4" />
                </button>
              )}
            </Alert>
          )}

          {isInitialLoading ? (
            <div className="vai-flex vai-justify-center vai-items-center">
              <LoadingSpinner className="vai-m-8" />
            </div>
          ) : referrals.length === 0 ? (
            <div className="vai-w-full vai-text-center vai-p-8 vai-text-zinc-700">
              <h3 className="vai-text-lg vai-font-medium vai-mb-2">
                <Trans>No referrals found</Trans>
              </h3>
              <p className="vai-text-sm vai-text-zinc-500">
                <Trans>
                  There are no referrals matching your current filter. Try
                  selecting a different status, specialty practice, or check
                  back later.
                </Trans>
              </p>
            </div>
          ) : (
            <ReferralsTable
              referrals={referrals}
              pagination={pagination}
              onNavigateToUnifiedAppointment={
                handleNavigateToUnifiedAppointment
              }
              onPageChange={handlePageChange}
              sortField={tableSortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              isSorting={isReferralsRefetching && lastAction === 'sort'}
              onResubmitReferral={handleResubmitReferral}
              onSubmitReferral={
                shouldShowOldReferralFlow ? handleSubmitReferral : undefined
              }
              onUnflagReferral={handleUnflagReferral}
            />
          )}
        </div>
      </PatientListPageLayout>

      {/* Create Referral Modal */}
      {shouldShowOldReferralFlow && (
        <Dialog
          open={isCreateReferralModalOpen}
          onOpenChange={(open) => {
            if (!open) {
              handleCloseModal();
            }
          }}
        >
          <DialogContent className="vai-max-w-[600px] vai-p-6">
            <DialogHeader className="vai-pb-2">
              <DialogTitle>
                <Trans>Create New Referral</Trans>
              </DialogTitle>
            </DialogHeader>

            <div className="vai-space-y-6">
              {/* Action Selection */}
              <div className="vai-space-y-3">
                <div className="vai-text-sm vai-font-medium vai-text-gray-700">
                  <Trans>What would you like to do?</Trans>
                </div>
                <div className="vai-grid vai-grid-cols-2 vai-gap-3">
                  <button
                    type="button"
                    onClick={() => setReferralAction('flag')}
                    className={`vai-relative vai-flex vai-flex-col vai-items-start vai-p-4 vai-border-2 vai-rounded-lg vai-transition-all vai-cursor-pointer ${
                      referralAction === 'flag'
                        ? 'vai-border-amber-600 vai-bg-amber-50'
                        : 'vai-border-gray-200 vai-bg-white hover:vai-border-gray-300'
                    }`}
                  >
                    <div className="vai-flex vai-items-center vai-gap-2 vai-mb-1">
                      <div
                        className={`vai-w-4 vai-h-4 vai-rounded-full vai-border-2 vai-flex vai-items-center vai-justify-center ${
                          referralAction === 'flag'
                            ? 'vai-border-amber-600'
                            : 'vai-border-gray-300'
                        }`}
                      >
                        {referralAction === 'flag' && (
                          <div className="vai-w-2 vai-h-2 vai-rounded-full vai-bg-amber-600" />
                        )}
                      </div>
                      <span
                        className={`vai-font-semibold vai-text-sm ${
                          referralAction === 'flag'
                            ? 'vai-text-amber-900'
                            : 'vai-text-gray-900'
                        }`}
                      >
                        <Trans>Referral Needed</Trans>
                      </span>
                    </div>
                    <p
                      className={`vai-text-xs vai-ml-6 ${
                        referralAction === 'flag'
                          ? 'vai-text-amber-700'
                          : 'vai-text-gray-600'
                      }`}
                    >
                      <Trans>Mark patient as needing a referral</Trans>
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setReferralAction('refer')}
                    className={`vai-relative vai-flex vai-flex-col vai-items-start vai-p-4 vai-border-2 vai-rounded-lg vai-transition-all vai-cursor-pointer ${
                      referralAction === 'refer'
                        ? 'vai-border-blue-600 vai-bg-blue-50'
                        : 'vai-border-gray-200 vai-bg-white hover:vai-border-gray-300'
                    }`}
                  >
                    <div className="vai-flex vai-items-center vai-gap-2 vai-mb-1">
                      <div
                        className={`vai-w-4 vai-h-4 vai-rounded-full vai-border-2 vai-flex vai-items-center vai-justify-center ${
                          referralAction === 'refer'
                            ? 'vai-border-blue-600'
                            : 'vai-border-gray-300'
                        }`}
                      >
                        {referralAction === 'refer' && (
                          <div className="vai-w-2 vai-h-2 vai-rounded-full vai-bg-blue-600" />
                        )}
                      </div>
                      <span
                        className={`vai-font-semibold vai-text-sm ${
                          referralAction === 'refer'
                            ? 'vai-text-blue-900'
                            : 'vai-text-gray-900'
                        }`}
                      >
                        <Trans>Send Referral</Trans>
                      </span>
                    </div>
                    <p
                      className={`vai-text-xs vai-ml-6 ${
                        referralAction === 'refer'
                          ? 'vai-text-blue-700'
                          : 'vai-text-gray-600'
                      }`}
                    >
                      <Trans>Create and submit a referral immediately</Trans>
                    </p>
                  </button>
                </div>
              </div>

              {/* Patient Search */}
              <div className="vai-space-y-2">
                <label
                  htmlFor="patient-search"
                  className="vai-text-sm vai-font-medium vai-text-gray-700"
                >
                  <Trans>Search Patient</Trans>
                </label>
                <Autocomplete
                  id="patient-search"
                  options={
                    patientSearchResults?.patients.map((patient) => {
                      const lastAppt = patient.lastAppointment
                        ? `${t`Last Visit`}: ${parseAndFormatDate(
                            patient.lastAppointment,
                            'Pp',
                          )}`
                        : null;
                      const dob = patient.dateOfBirth
                        ? `${t`Date of Birth`}: ${parseAndFormatDate(
                            patient.dateOfBirth,
                          )}`
                        : null;
                      const details = [lastAppt, dob]
                        .filter(Boolean)
                        .join(' • ');
                      return {
                        label: formatPatientName(patient.name),
                        secondaryLabel: details || undefined,
                        value: patient.imagingPatientVideaId,
                      };
                    }) || []
                  }
                  value={formatPatientName(selectedPatient?.name || '')}
                  onChange={(newValue) => {
                    // Find the patient from the search results
                    const patient = patientSearchResults?.patients.find(
                      (p) => p.imagingPatientVideaId === newValue,
                    );
                    setSelectedPatient(patient || null);
                  }}
                  search={patientSearchTerm}
                  onInputChange={(newValue) => {
                    setPatientSearchTerm(newValue);
                    if (!newValue) {
                      setSelectedPatient(null);
                    }
                  }}
                  debounceMs={300}
                  placeholder={t`Start typing patient name...`}
                  noOptionsText={
                    patientSearchTerm.length < 2
                      ? t`Type at least 2 characters to search`
                      : isSearchingPatients
                        ? t`Searching...`
                        : t`No patients found`
                  }
                  dataTestId="patient-search-input"
                />
              </div>

              {/* Selected Patient Display */}
              {selectedPatient && (
                <div
                  className={`vai-rounded-lg vai-border-2 vai-p-4 ${
                    referralAction === 'refer'
                      ? 'vai-bg-blue-50 vai-border-blue-200'
                      : 'vai-bg-amber-50 vai-border-amber-200'
                  }`}
                >
                  <div
                    className={`vai-text-sm vai-font-semibold vai-mb-2 ${
                      referralAction === 'refer'
                        ? 'vai-text-blue-900'
                        : 'vai-text-amber-900'
                    }`}
                  >
                    <Trans>Selected Patient</Trans>
                  </div>
                  <div
                    className={`vai-text-sm ${
                      referralAction === 'refer'
                        ? 'vai-text-blue-800'
                        : 'vai-text-amber-800'
                    }`}
                  >
                    <div className="vai-font-medium vai-text-base">
                      {formatPatientName(selectedPatient.name)}
                    </div>
                    {(selectedPatient.lastAppointment ||
                      selectedPatient.dateOfBirth) && (
                      <div className="vai-text-xs vai-mt-2 vai-space-y-1">
                        {selectedPatient.lastAppointment && (
                          <div>
                            <span className="vai-font-medium">
                              <Trans>Last Appointment:</Trans>
                            </span>{' '}
                            {parseAndFormatDate(
                              selectedPatient.lastAppointment,
                              'Pp',
                            )}
                          </div>
                        )}
                        {selectedPatient.dateOfBirth && (
                          <div>
                            <span className="vai-font-medium">
                              <Trans>Date of Birth:</Trans>
                            </span>{' '}
                            {parseAndFormatDate(selectedPatient.dateOfBirth)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Helper Text */}
              {!selectedPatient && (
                <div className="vai-text-sm vai-text-gray-600 vai-bg-gray-50 vai-p-3 vai-rounded-lg">
                  {referralAction === 'refer' ? (
                    <Trans>
                      Search for a patient by name to send a referral.
                    </Trans>
                  ) : (
                    <Trans>
                      Search for a patient by name to flag for referral.
                    </Trans>
                  )}
                </div>
              )}

              {/* Next Button */}
              <div className="vai-flex vai-justify-end vai-pt-2 vai-border-t vai-border-gray-200">
                <Button
                  onClick={handleNext}
                  disabled={!selectedPatient}
                  className={`vai-min-w-[120px] ${
                    referralAction === 'refer'
                      ? 'vai-bg-blue-600 hover:vai-bg-blue-700'
                      : 'vai-bg-amber-600 hover:vai-bg-amber-700'
                  }`}
                >
                  <Trans>Next</Trans>
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Specialty Selection Modal for Flag Action */}
      {shouldShowOldReferralFlow && (
        <SpecialtySelectionModal
          isOpen={isSpecialtyModalOpen}
          onClose={() => {
            setIsSpecialtyModalOpen(false);
            // Reset the create referral modal state when closing
            handleCloseModal();
          }}
          onConfirm={handleSpecialtyConfirm}
          isLoading={isCreatingReferral}
        />
      )}

      {/* Referral Modal for Send Referral Action */}
      {shouldShowOldReferralFlow && selectedPatient && (
        <ReferralModal
          isOpen={isReferralModalOpen}
          patientData={selectedPatient}
          onClose={() => {
            setIsReferralModalOpen(false);
            // Also close the main modal after successful referral
            handleCloseModal();
          }}
          hideDownloadButton={true}
        />
      )}

      {/* Submit Referral Confirmation Modal */}
      {shouldShowOldReferralFlow && selectedReferralForSubmit && (
        <>
          <SubmitReferralModal
            isOpen={isSubmitConfirmModalOpen}
            referral={selectedReferralForSubmit}
            onClose={handleCloseSubmitModal}
            onConfirm={handleSubmitConfirm}
          />

          {/* Full Referral Form Modal */}
          <ReferralModal
            isOpen={isSubmitReferralModalOpen}
            unifiedAppointmentId={
              selectedReferralForSubmit.unifiedAppointmentId
            }
            patientData={
              {
                imagingPatientVideaId:
                  selectedReferralForSubmit.imagingPatientVideaId || '',
                pmsPatientVideaId:
                  selectedReferralForSubmit.pmsPatientVideaId || undefined,
                name: `${selectedReferralForSubmit.patientFirstName} ${selectedReferralForSubmit.patientLastName}`,
              } as PatientSearchResult
            }
            onClose={() => {
              setIsSubmitReferralModalOpen(false);
              setSelectedReferralForSubmit(null);
              queryClient.invalidateQueries(['referrals']);
            }}
            hideDownloadButton={true}
          />
        </>
      )}
    </>
  );
};
